import { useState } from 'react';
import { TreatmentPlan } from '../../types';
import { Calendar, CheckCircle, Clock, AlertCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

export default function TreatmentRecords({ treatmentPlans }: { treatmentPlans: TreatmentPlan[] }) {
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);

  const getStatusColor = (status: TreatmentPlan['status']) => {
    switch (status) {
      case 'proposed':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: TreatmentPlan['status']) => {
    switch (status) {
      case 'proposed':
        return <AlertCircle className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'in-progress':
        return <Clock className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'declined':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (treatmentPlans.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-600">No treatment plans recorded</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Treatment Plans & Records</h3>
      <div className="space-y-4">
        {treatmentPlans.map((plan) => {
          const isExpanded = expandedPlan === plan.id;

          return (
            <div
              key={plan.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Plan Header */}
              <button
                onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-4 flex-1 text-left">
                  <div className={`p-2 rounded-lg ${getStatusColor(plan.status)}`}>
                    {getStatusIcon(plan.status)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{plan.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Created: {new Date(plan.dateCreated).toLocaleDateString()}</span>
                      <span>Updated: {new Date(plan.dateUpdated).toLocaleDateString()}</span>
                      <span className={`px-2 py-1 rounded-full font-medium ${getStatusColor(plan.status)}`}>
                        {plan.status}
                      </span>
                    </div>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Plan Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <h5 className="font-semibold text-gray-900 mb-3">Procedures</h5>
                  <div className="space-y-2">
                    {plan.procedures.map((procedure) => (
                      <div
                        key={procedure.id}
                        className="bg-white rounded-lg p-4 border border-gray-200 flex items-start justify-between"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h6 className="font-medium text-gray-900">{procedure.name}</h6>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(procedure.priority)}`}>
                              {procedure.priority}
                            </span>
                          </div>
                          {procedure.toothNumber && (
                            <p className="text-sm text-gray-600">Tooth #{procedure.toothNumber}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{procedure.duration} min</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
